import { inject } from '@angular/core';

import { injectMutation } from '@tanstack/angular-query-experimental';

import { firstValueFrom } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { ToastService } from '@services/toast.service';
import {
  UserGroupApiService,
  UserGroupData,
} from '../services/user-group-api.service';

/** One user-group write: the editor payload and which of the two endpoints it goes to. */
export interface ManageUserGroupVariables {
  data: UserGroupData;
  isCreate: boolean;
}

/**
 * Create (PUT /user_group/create) or update (POST /user_group/update) a user
 * group - the caller says which of the two it is, the backend guards them
 * differently (SEN-5297).
 *
 * No query invalidation: the user-groups list is a server-side ag-grid, not a
 * query.
 */
export function injectManageUserGroupMutation() {
  const userGroupApi = inject(UserGroupApiService);
  return injectMutation(() => ({
    mutationFn: ({ data, isCreate }: ManageUserGroupVariables) =>
      firstValueFrom(
        isCreate
          ? userGroupApi.createUserGroup(data)
          : userGroupApi.updateUserGroup(data),
      ),
  }));
}

/**
 * Delete a user group (DELETE /user_group/:id). No invalidation - the
 * user-groups list is a server-side ag-grid, not a query.
 */
export function injectDeleteUserGroupMutation() {
  const userGroupApi = inject(UserGroupApiService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (userGroupId: string) =>
      firstValueFrom(userGroupApi.deleteUserGroup(userGroupId)),
    onSuccess: () =>
      toast.showSuccess(
        translate.instant('admin-module.msg.user-group-deleted-successfully'),
      ),
  }));
}
